/** Lifecycle of the merchant-managed search-suggestion categories content asset. */
export type ManualCategoriesStatus = 'absent' | 'pending' | 'empty' | 'ready';

/**
 * Explicit search suggestions panel mode.
 * - `closed` — panel hidden
 * - `default` — empty/short query: recent searches + merchant categories
 * - `typed` — query ≥ min length with API suggestions (typed viewport UI lands in a follow-up)
 */
export type SearchViewport = 'closed' | 'default' | 'typed';

/** Open panel modes — what `Suggestions` receives when the panel is visible. */
export type OpenSearchViewport = Exclude<SearchViewport, 'closed'>;

export interface SearchApiSuggestionsSnapshot {
    categorySuggestions: unknown[];
    categoryPhraseSuggestions: unknown[];
    productSuggestions: unknown[];
    popularSearchSuggestions?: unknown[];
}

export interface ResolveSearchViewportInput {
    query: string;
    recentSearchCount: number;
    categoriesStatus: ManualCategoriesStatus;
    apiSuggestions: SearchApiSuggestionsSnapshot | null;
    isFocused: boolean;
    /** User explicitly dismissed the panel (outside click, Escape, close button). */
    dismissed: boolean;
    minQueryLength?: number;
}

/** Whether the default (empty/short query) suggestions viewport may open or stay open. */
export function isDefaultViewportEligible(
    recentSearchCount: number,
    categoriesStatus: ManualCategoriesStatus
): boolean {
    return recentSearchCount > 0 || categoriesStatus === 'pending' || categoriesStatus === 'ready';
}

/** Whether typed-query API suggestions have at least one renderable result group. */
export function hasSearchApiSuggestions(apiSuggestions: SearchApiSuggestionsSnapshot | null): boolean {
    if (!apiSuggestions) {
        return false;
    }

    return (
        apiSuggestions.categorySuggestions.length > 0 ||
        apiSuggestions.categoryPhraseSuggestions.length > 0 ||
        apiSuggestions.productSuggestions.length > 0 ||
        (apiSuggestions.popularSearchSuggestions?.length ?? 0) > 0
    );
}

/** Single source of truth for which search suggestions viewport (if any) should be visible. */
export function resolveSearchViewport({
    query,
    recentSearchCount,
    categoriesStatus,
    apiSuggestions,
    isFocused,
    dismissed,
    minQueryLength = 3,
}: ResolveSearchViewportInput): SearchViewport {
    if (!isFocused || dismissed) {
        return 'closed';
    }

    const isDefaultQuery = query.trim().length < minQueryLength;

    if (!isDefaultQuery) {
        return hasSearchApiSuggestions(apiSuggestions) ? 'typed' : 'closed';
    }

    return isDefaultViewportEligible(recentSearchCount, categoriesStatus) ? 'default' : 'closed';
}
